import { getClassNames } from '../../../_utils/classnames'
import React, { useMemo, useRef } from 'react'
import {
    AppMessageTemplateButtonConfig,
    MutableAppMessageTemplateButtonConfig,
} from '@pushly/models/lib/structs/app-messages/app-message-template-button-config'
import { CtaButtonType, CtaGroupTuple } from '../types'
import { titleCase } from '../../../_utils/utils'
import { AppMessageActionType } from '@pushly/models/lib/enums/app-messages/app-message-action-type'
import { DeleteOutlined } from '@ant-design/icons'
import { DEFAULT_CTA_BG_COLOR, DEFAULT_CTA_FG_COLOR } from '../constants'
import { enumAsSelectOptions } from '../../../_utils/antd'
import { AppMessageActionStyle } from '@pushly/models/lib/enums/app-messages/app-message-action-style'
import { AppMessageTemplateStyle } from '@pushly/models/lib/enums'
import { useAppMessageBuilderContext } from '../../../features/app-messages/context'
import { Position } from '@pushly/models/lib/enums/position'
import { ConditionalTooltip } from '../../conditional-popover/conditional-popover'
import { ColorSwatchField, MacroType } from '@pushly/aqe/lib/components'
import { Radio, Select } from 'antd'
import TemplatizedTextField, {
    InputFocusRef,
} from '@pushly/aqe/lib/components/templatized-text-field/templatized-text-field'
import { AppMessageCtaRow } from './app-message-cta-row'

import './styles/app-message-cta-builder.scss'

type AppMessageCtaBuilderProps = {
    value: CtaGroupTuple
    type: CtaButtonType
    rootCtas: CtaGroupTuple[]
    templateStyle: AppMessageTemplateStyle
    onChange: (change: Partial<AppMessageTemplateButtonConfig>, action: CtaButtonType) => void
    disabled: boolean
    expanded: boolean
    onExpandedChange: (expanded: boolean) => void
}

/** The URL a button opens can carry tracking macros, but not arbitrary profile data. */
const UTM_MACRO_TYPES: MacroType[] = ['domain', 'device', 'location', 'app_message', 'custom']

export const AppMessageCtaBuilder = (props: AppMessageCtaBuilderProps) => {
    const { disabled, value, rootCtas, templateStyle, expanded, onExpandedChange } = props

    const [context, dispatch] = useAppMessageBuilderContext()
    const templateValue = context.variants[context.selectedVariantIdx].template

    // 2nd action not available for banners
    if (props.type === CtaButtonType.SECONDARY && templateStyle === AppMessageTemplateStyle.BANNER) return null

    const labelRef = useRef<InputFocusRef>(null)

    const handleChange = (change: Partial<AppMessageTemplateButtonConfig>) => {
        // cannot disable Primary when Secondary contains a value
        if ('enabled' in change && props.type === CtaButtonType.PRIMARY && !change.enabled && rootCtas[1][1].enabled) {
            return
        }

        props.onChange(change, props.type)
    }

    const localValue = value[1]
    const opposingAction = rootCtas[props.type === CtaButtonType.PRIMARY ? 0 : 1][1]
    const defaultValue = localValue.enabled
        ? localValue
        : ({
              enabled: true,
              fgColor: DEFAULT_CTA_FG_COLOR,
              bgColor: DEFAULT_CTA_BG_COLOR,
              action: opposingAction.enabled
                  ? opposingAction.action === AppMessageActionType.CLOSE
                      ? AppMessageActionType.OPEN_URL
                      : AppMessageActionType.CLOSE
                  : AppMessageActionType.OPEN_URL,
          } as MutableAppMessageTemplateButtonConfig)

    const [siblingCtaTuple] = rootCtas
        .filter(([type, _]) => {
            return type !== props.type
        })
        .map(([_, cta]) => cta)

    const isBannerButtonUnavailable =
        templateValue &&
        templateValue.style === AppMessageTemplateStyle.BANNER &&
        templateValue?.image.enabled &&
        templateValue.image.position !== Position.LEFT

    const actionOptions = useMemo(() => {
        const options = [
            { label: 'Open URL', value: AppMessageActionType.OPEN_URL },
            {
                label: 'Prompt For Notification Permissions',
                value: AppMessageActionType.PROMPT_NOTIFICATIONS,
                disabled: siblingCtaTuple.action === AppMessageActionType.PROMPT_NOTIFICATIONS,
            },
        ]

        if (templateStyle !== AppMessageTemplateStyle.BANNER) {
            options.push({
                label: 'Dismiss',
                value: AppMessageActionType.CLOSE,
                disabled: siblingCtaTuple.action === AppMessageActionType.CLOSE,
            })
        }

        return options
    }, [templateStyle, siblingCtaTuple.action])

    const handleEnabledChange = (enabled: boolean) => {
        if (enabled) {
            if (templateStyle === AppMessageTemplateStyle.BANNER) {
                const ctaKey = props.type === CtaButtonType.PRIMARY ? 'ctaPrimary' : 'ctaSecondary'

                // Two field patches rather than one template replacement, so
                // each merges against live state instead of a value read at
                // render time.
                if (templateValue?.image.enabled && templateValue.image.position) {
                    dispatch({
                        type: 'patch',
                        entity: 'template-field',
                        data: { field: 'image', change: { position: Position.LEFT } },
                    })
                }

                dispatch({
                    type: 'patch',
                    entity: 'template-field',
                    data: {
                        field: ctaKey,
                        change: {
                            // Spread first, so this branch differs from the one
                            // below only where a banner genuinely differs. Listing
                            // the banner's fields alone left the colours and the
                            // action out, which the model requires once enabled.
                            ...defaultValue,
                            enabled,
                            style: localValue.style ?? AppMessageActionStyle.TAB,
                            label: localValue.label ?? '›',
                        },
                    },
                })
            } else {
                handleChange({ ...defaultValue, enabled })
            }

            onExpandedChange(true)

            if (labelRef.current) {
                labelRef?.current.focusInput()
            }
        } else {
            handleChange({ enabled })
            onExpandedChange(false)
        }
    }

    // The primary is the anchor of the pair: removing it while a secondary is on
    // would leave the message with only a secondary action.
    const primaryLockedBySecondary = props.type === CtaButtonType.PRIMARY && rootCtas[1][1].enabled
    const removeTooltip = primaryLockedBySecondary
        ? 'Primary Button cannot be removed while a Secondary button is present.'
        : undefined

    const actionSummary = useMemo(() => {
        switch (localValue.action) {
            case AppMessageActionType.PROMPT_NOTIFICATIONS:
                return 'Prompt For Notification Permissions'
            case AppMessageActionType.CLOSE:
                return 'Dismiss'
            default:
                return localValue.url ? `Open URL · ${localValue.url}` : 'Open URL'
        }
    }, [localValue.action, localValue.url])

    // Labels above their controls, two to a row: the pairs that read together —
    // what the button says and what it does, then its two colours — sit side by
    // side, and the URL takes the full width because a URL is long.
    const settings = (
        <div className="cta-form">
            {templateStyle === AppMessageTemplateStyle.BANNER && (
                <div className="cta-field">
                    <span className="cta-field-label">Button Style</span>
                    <Radio.Group
                        buttonStyle="solid"
                        optionType="button"
                        size="small"
                        disabled={disabled}
                        value={localValue.style}
                        onChange={(ev) => {
                            handleChange({ style: ev.target.value })
                        }}
                    >
                        {enumAsSelectOptions(AppMessageActionStyle).map(({ value: style, label }) => (
                            <Radio.Button key={style} value={style}>
                                {label}
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                </div>
            )}

            {templateStyle !== AppMessageTemplateStyle.BANNER && (
                <div className="cta-field">
                    <TemplatizedTextField
                        ref={labelRef}
                        label="Button Text"
                        formItemProps={{ layout: 'vertical' }}
                        value={localValue.label}
                        disabled={disabled}
                        onChange={(label) => {
                            handleChange({ label })
                        }}
                        placeholder={`${titleCase(props.type)} Call-to-Action`}
                    />
                </div>
            )}

            <div className="cta-field">
                <span className="cta-field-label">Action Type</span>
                <Select<AppMessageActionType>
                    aria-label="Action type"
                    disabled={disabled}
                    value={localValue.action ?? AppMessageActionType.OPEN_URL}
                    defaultValue={AppMessageActionType.OPEN_URL}
                    onChange={(action) => handleChange({ action })}
                    options={actionOptions}
                />
            </div>

            {localValue.action === AppMessageActionType.OPEN_URL && (
                <div className="cta-field cta-field-full">
                    <TemplatizedTextField
                        label="URL"
                        formItemProps={{ layout: 'vertical' }}
                        disabled={disabled}
                        hideEmojis={true}
                        macroTypes={UTM_MACRO_TYPES}
                        className={getClassNames(null, 'url-input-group', { disabled })}
                        placeholder="app://..."
                        value={localValue.url}
                        onChange={(v) => handleChange({ url: v })}
                    />
                </div>
            )}

            <div className="cta-field">
                <span className="cta-field-label">Background</span>
                <ColorSwatchField
                    aria-label="Button background color"
                    disabled={disabled}
                    value={localValue.bgColor ?? defaultValue.bgColor}
                    onChange={(color) => handleChange({ bgColor: color })}
                />
            </div>

            <div className="cta-field">
                <span className="cta-field-label">Text</span>
                <ColorSwatchField
                    aria-label="Button text color"
                    disabled={disabled}
                    value={localValue.fgColor ?? defaultValue.fgColor}
                    onChange={(color) => handleChange({ fgColor: color })}
                />
            </div>
        </div>
    )

    // Removing a button is the same operation the model calls disabling it: the
    // row goes away and the add control returns in its place.
    const removeBlocked = primaryLockedBySecondary || disabled

    return (
        <ConditionalTooltip
            title="A button cannot be enabled on a Banner Message when the Image is Position Right. Please select Position Left first to enable a button"
            condition={isBannerButtonUnavailable ?? false}
        >
            <AppMessageCtaRow
                className={getClassNames(null, {
                    'cta-primary': props.type === CtaButtonType.PRIMARY,
                    'cta-secondary': props.type === CtaButtonType.SECONDARY,
                })}
                label={`${titleCase(props.type)} Button`}
                tag={props.type === CtaButtonType.PRIMARY ? 'Primary' : 'Secondary'}
                summary={actionSummary}
                foregroundColor={localValue.fgColor ?? defaultValue.fgColor}
                backgroundColor={localValue.bgColor ?? defaultValue.bgColor}
                expanded={expanded}
                onExpandedChange={onExpandedChange}
                remove={{
                    onRemove: () => handleEnabledChange(false),
                    disabled: removeBlocked,
                    tooltip: removeTooltip,
                    'aria-label': `Remove ${titleCase(props.type).toLowerCase()} button`,
                }}
            >
                {settings}
            </AppMessageCtaRow>
        </ConditionalTooltip>
    )
}
